import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Stack,
  Badge,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Warning as AlertCircle,
  CheckCircle,
  AccessTime as Clock,
  AttachMoney as DollarSign,
  Phone,
  People as Users,
  FilterList as Filter,
  Search,
  Add as Plus,
  Visibility as Eye,
  Edit,
  Telegram as TelegramIcon,
  WhatsApp as WhatsAppIcon,
  Delete as DeleteIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';

const API_BASE_URL = getApiUrl();

interface ControlLead {
  id: string;
  contact_id: string;
  lead_name: string;
  lead_email?: string;
  lead_phone?: string;
  status: 'pending' | 'processed' | 'completed';
  control_type: 'refund_request' | 'exclusive_call';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  notes?: string;
  created_at: string;
  updated_at: string;
  contact?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
  };
}

interface KirvanoProduct {
  id: string;
  name: string;
  price: number;
  event_type: string;
  purchase_date: string;
  sale_id: string;
  contact: {
    id: string;
    name: string;
    phone?: string;
    email?: string;
  };
}

interface KirvanoProductsResponse {
  success: boolean;
  data: {
    email: string;
    products: KirvanoProduct[];
    purchase_history: any[];
    statistics: {
      total_products: number;
      total_purchases: number;
      total_spent: number;
      event_types_found: string[];
      date_range: {
        first_purchase: string | null;
        last_purchase: string | null;
      };
    };
    top_products: Array<{
      name: string;
      count: number;
    }>;
  };
  meta: {
    search_params: any;
    found: number;
    total_webhooks: number;
  };
}

interface ControlStats {
  total: number;
  pending: number;
  processed: number;
  completed: number;
  refund_requests: number;
  exclusive_calls: number;
  by_priority: {
    low: number;
    normal: number;
    high: number;
    urgent: number;
  };
}

const ControlDashboard: React.FC = () => {
  const [leads, setLeads] = useState<ControlLead[]>([]);
  const [stats, setStats] = useState<ControlStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    control_type: '',
    priority: '',
    search: ''
  });
  const [selectedLead, setSelectedLead] = useState<ControlLead | null>(null);
  const [processNotes, setProcessNotes] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [kirvanoProducts, setKirvanoProducts] = useState<KirvanoProduct[]>([]);
  const [kirvanoStats, setKirvanoStats] = useState<any>(null);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [leadConversations, setLeadConversations] = useState<Map<string, { hasWhatsApp: boolean; hasTelegram: boolean }>>(new Map());

  // Hooks devem estar no topo do componente
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Carregar dados
  useEffect(() => {
    loadData();
  }, [filters]);

  // Verificar conversas para cada lead após carregar dados
  useEffect(() => {
    if (leads.length > 0) {
      checkLeadConversations();
    }
  }, [leads]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Carregar leads
      const leadsResponse = await axios.get(`${API_BASE_URL}/control/leads`, {
        params: {
          ...filters,
          limit: 100
        }
      });
      setLeads(leadsResponse.data.data);

      // Carregar estatísticas
      const statsResponse = await axios.get(`${API_BASE_URL}/control/stats`);
      setStats(statsResponse.data.data);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadKirvanoProducts = async (email: string) => {
    if (!email) return;
    
    try {
      setLoadingProducts(true);
      const response = await axios.get(`${API_BASE_URL}/kirvano/products/by-email`, {
        params: {
          email: email,
          event_types: 'SALE_APPROVED,APPROVED',
          limit: 100
        }
      });
      
      if (response.data.success) {
        setKirvanoProducts(response.data.data.products);
        setKirvanoStats(response.data.data.statistics);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos Kirvano:', error);
      setKirvanoProducts([]);
      setKirvanoStats(null);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleProcessLead = async (leadId: string) => {
    try {
      await axios.post(`${API_BASE_URL}/control/leads/${leadId}/process`, {
        notes: processNotes
      });
      
      setProcessNotes('');
      setSelectedLead(null);
      setOpenDialog(false);
      loadData(); // Recarregar dados
      
    } catch (error) {
      console.error('Erro ao processar lead:', error);
    }
  };

  const handleViewTelegramConversation = (contactId: string) => {
    // Navegar para o Telegram com o contactId
    navigate(`/inbox/telegram?contactId=${contactId}`);
  };

  // Verificar quais canais têm conversas para cada lead
  const checkLeadConversations = async () => {
    try {
      const contactIds = leads.map(lead => lead.contact_id);
      if (contactIds.length === 0) return;

      // Verificar conversas no WhatsApp
      const whatsappResponse = await axios.get(`${API_BASE_URL}/conversations`, {
        params: {
          channel: 'whatsapp',
          limit: 1000
        }
      });

      // Verificar conversas no Telegram
      const telegramResponse = await axios.get(`${API_BASE_URL}/conversations`, {
        params: {
          channel: 'telegram',
          limit: 1000
        }
      });

      const whatsappConversations = whatsappResponse.data.success ? whatsappResponse.data.data || [] : [];
      const telegramConversations = telegramResponse.data.success ? telegramResponse.data.data || [] : [];

      const conversationsMap = new Map<string, { hasWhatsApp: boolean; hasTelegram: boolean }>();

      contactIds.forEach(contactId => {
        const hasWhatsApp = whatsappConversations.some((conv: any) => conv.contact_id === contactId);
        const hasTelegram = telegramConversations.some((conv: any) => conv.contact_id === contactId);
        conversationsMap.set(contactId, { hasWhatsApp, hasTelegram });
      });

      setLeadConversations(conversationsMap);
    } catch (error) {
      console.error('Erro ao verificar conversas dos leads:', error);
    }
  };

  const handleViewWhatsAppConversation = (contactId: string) => {
    navigate(`/inbox/whatsapp?contactId=${contactId}`);
  };

  const handleUpdateStatus = async (leadId: string) => {
    try {
      await axios.put(`${API_BASE_URL}/control/leads/${leadId}`, {
        status: newStatus
      });
      
      setNewStatus('');
      setSelectedLead(null);
      setOpenStatusDialog(false);
      loadData(); // Recarregar dados
      
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  const handleDeleteLead = async (leadId: string) => {
    try {
      await axios.delete(`${API_BASE_URL}/control/leads/${leadId}`);
      
      setSelectedLead(null);
      setOpenDeleteDialog(false);
      loadData(); // Recarregar dados
      
    } catch (error) {
      console.error('Erro ao deletar lead:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'processed':
        return 'info';
      case 'completed':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'primary';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getControlTypeColor = (type: string) => {
    switch (type) {
      case 'refund_request':
        return 'error';
      case 'exclusive_call':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      backgroundColor: 'transparent', 
      minHeight: '100vh',
      color: 'text.primary',
      position: 'relative',
      zIndex: 1,
    }}>
      {/* Header com título */}
      <Box sx={{ 
        mb: { xs: 2, md: 3 }, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', md: 'center' },
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 0 },
        padding: { xs: '16px', md: '20px 24px' },
        position: 'relative',
        zIndex: 1,
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            color: '#000000',
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            mb: 0.5,
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <DollarSign sx={{ fontSize: { xs: 24, md: 32 }, color: '#f44336' }} />
            Reembolso
          </Typography>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
          }}>
            Gerencie solicitações de reembolso e agendamento de calls exclusivas
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="contained" 
            startIcon={<Plus />}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              color: '#000000',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Novo Lead
          </Button>
        </Box>
      </Box>

      {/* Cards de Estatísticas */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <Card className="glass-blue-card" sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      Total de Leads
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#000000' }}>
                      {stats.total}
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: '#f44336', 
                    width: 56, 
                    height: 56,
                    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)'
                  }}>
                    <Users sx={{ fontSize: 28 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Card className="glass-blue-card" sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      Pendentes
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#000000' }}>
                      {stats.pending}
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: '#ff9800', 
                    width: 56, 
                    height: 56,
                    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)'
                  }}>
                    <Clock sx={{ fontSize: 28 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Card className="glass-blue-card" sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      Reembolsos
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#000000' }}>
                      {stats.refund_requests}
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: '#f44336', 
                    width: 56, 
                    height: 56,
                    boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)'
                  }}>
                    <DollarSign sx={{ fontSize: 28 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} lg={3}>
            <Card className="glass-blue-card" sx={{
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
              }
            }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      Calls Exclusivas
                    </Typography>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#000000' }}>
                      {stats.exclusive_calls}
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: '#4caf50', 
                    width: 56, 
                    height: 56,
                    boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)'
                  }}>
                    <Phone sx={{ fontSize: 28 }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filtros */}
      <Card className="glass-blue-card" sx={{ 
        mb: 3,
      }}>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <Avatar sx={{ bgcolor: '#f44336', width: 32, height: 32 }}>
                <Filter sx={{ fontSize: 18 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000' }}>
                Filtros Avançados
              </Typography>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#64748b' }}>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  label="Status"
                  sx={{
                    color: '#000000',
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(59, 130, 246, 0.08)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(59, 130, 246, 0.4)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                      },
                      '&.Mui-focused': {
                        borderColor: 'rgba(59, 130, 246, 0.6)',
                        boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                      '& select': {
                        color: '#000000',
                      },
                    },
                    '& .MuiSelect-icon': {
                      color: '#64748b',
                    },
                  }}
                >
                  <MenuItem value="">Todos os Status</MenuItem>
                  <MenuItem value="pending">⏳ Pendente</MenuItem>
                  <MenuItem value="processed">🔄 Processado</MenuItem>
                  <MenuItem value="completed">✅ Concluído</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#64748b' }}>Tipo de Solicitação</InputLabel>
                <Select
                  value={filters.control_type}
                  onChange={(e) => setFilters({...filters, control_type: e.target.value})}
                  label="Tipo de Solicitação"
                  sx={{
                    color: '#000000',
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(59, 130, 246, 0.08)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(59, 130, 246, 0.4)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                      },
                      '&.Mui-focused': {
                        borderColor: 'rgba(59, 130, 246, 0.6)',
                        boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                      '& select': {
                        color: '#000000',
                      },
                    },
                    '& .MuiSelect-icon': {
                      color: '#64748b',
                    },
                  }}
                >
                  <MenuItem value="">Todos os Tipos</MenuItem>
                  <MenuItem value="refund_request">💰 Reembolso</MenuItem>
                  <MenuItem value="exclusive_call">📞 Call Exclusiva</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#64748b' }}>Prioridade</InputLabel>
                <Select
                  value={filters.priority}
                  onChange={(e) => setFilters({...filters, priority: e.target.value})}
                  label="Prioridade"
                  sx={{
                    color: '#000000',
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(59, 130, 246, 0.08)',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'rgba(59, 130, 246, 0.4)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                      },
                      '&.Mui-focused': {
                        borderColor: 'rgba(59, 130, 246, 0.6)',
                        boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                      },
                      '& fieldset': {
                        border: 'none',
                      },
                      '& select': {
                        color: '#000000',
                      },
                    },
                    '& .MuiSelect-icon': {
                      color: '#64748b',
                    },
                  }}
                >
                  <MenuItem value="">Todas as Prioridades</MenuItem>
                  <MenuItem value="urgent">🔴 Urgente</MenuItem>
                  <MenuItem value="high">🟠 Alta</MenuItem>
                  <MenuItem value="normal">🔵 Normal</MenuItem>
                  <MenuItem value="low">⚪ Baixa</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="🔍 Buscar Leads"
                placeholder="Nome, email, telefone..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(59, 130, 246, 0.08)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    borderRadius: '8px',
                    color: '#000000',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(59, 130, 246, 0.4)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                    },
                    '&.Mui-focused': {
                      borderColor: 'rgba(59, 130, 246, 0.6)',
                      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                    },
                    '& fieldset': {
                      border: 'none',
                    },
                    '& input': {
                      color: '#000000',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                    '&.Mui-focused': {
                      color: 'rgba(59, 130, 246, 0.8)',
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card className="glass-blue-card" sx={{
        
        overflow: 'hidden'
      }}>
        <CardHeader 
          title={
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ bgcolor: '#f44336', width: 40, height: 40 }}>
                <Users sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000' }}>
                  Leads de Reembolso
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Gerencie todas as solicitações em um só lugar
                </Typography>
              </Box>
            </Box>
          }
        />
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}>
                  <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Lead</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Prioridade</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Data</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leads.map((lead, index) => (
                  <TableRow 
                    key={lead.id}
                    sx={{ 
                      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                      borderBottom: index < leads.length - 1 ? '1px solid' : 'none',
                      borderColor: 'rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ 
                          bgcolor: lead.control_type === 'refund_request' ? '#f44336' : '#4caf50',
                          width: 40,
                          height: 40
                        }}>
                          {lead.control_type === 'refund_request' ? 
                            <DollarSign sx={{ fontSize: 20 }} /> : 
                            <Phone sx={{ fontSize: 20 }} />
                          }
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#000000' }}>
                            {lead.lead_name}
                          </Typography>
                          {lead.lead_email && (
                            <Typography variant="body2" color="text.secondary">
                              📧 {lead.lead_email}
                            </Typography>
                          )}
                          {lead.lead_phone && (
                            <Typography variant="body2" color="text.secondary">
                              📱 {lead.lead_phone}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={lead.status === 'pending' ? '⏳ Pendente' : 
                               lead.status === 'processed' ? '🔄 Processado' : '✅ Concluído'}
                        color={getStatusColor(lead.status) as any}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={lead.control_type === 'refund_request' ? '💰 Reembolso' : '📞 Call Exclusiva'}
                        color={getControlTypeColor(lead.control_type) as any}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={lead.priority === 'urgent' ? '🔴 Urgente' :
                               lead.priority === 'high' ? '🟠 Alta' :
                               lead.priority === 'normal' ? '🔵 Normal' : '⚪ Baixa'}
                        color={getPriorityColor(lead.priority) as any}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(lead.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Ver detalhes">
                          <IconButton
                            onClick={() => {
                              setSelectedLead(lead);
                              setOpenDialog(true);
                              // Carregar produtos Kirvano se houver email
                              if (lead.lead_email) {
                                loadKirvanoProducts(lead.lead_email);
                              }
                            }}
                            sx={{
                              bgcolor: '#f44336',
                              color: '#000000',
                              '&:hover': {
                                bgcolor: '#d32f2f',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <Eye />
                          </IconButton>
                        </Tooltip>
                        {(() => {
                          const conversationInfo = leadConversations.get(lead.contact_id);
                          const hasWhatsApp = conversationInfo?.hasWhatsApp || false;
                          const hasTelegram = conversationInfo?.hasTelegram || false;

                          // Mostrar WhatsApp se tiver conversa no WhatsApp e não tiver no Telegram
                          if (hasWhatsApp && !hasTelegram) {
                            return (
                              <Tooltip title="Ir para conversa no WhatsApp">
                                <IconButton
                                  onClick={() => handleViewWhatsAppConversation(lead.contact_id)}
                                  sx={{
                                    bgcolor: '#25D366',
                                    color: '#000000',
                                    '&:hover': {
                                      bgcolor: '#20BA5A',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <WhatsAppIcon />
                                </IconButton>
                              </Tooltip>
                            );
                          }

                          // Mostrar Telegram se tiver conversa no Telegram
                          if (hasTelegram) {
                            return (
                              <Tooltip title="Ir para conversa no Telegram">
                                <IconButton
                                  onClick={() => handleViewTelegramConversation(lead.contact_id)}
                                  sx={{
                                    bgcolor: '#0088cc',
                                    color: '#000000',
                                    '&:hover': {
                                      bgcolor: '#006699',
                                      transform: 'scale(1.1)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <TelegramIcon />
                                </IconButton>
                              </Tooltip>
                            );
                          }

                          // Não mostrar botão se não tiver conversa em nenhum canal
                          return null;
                        })()}
                        <Tooltip title="Alterar status">
                          <IconButton
                            onClick={() => {
                              setSelectedLead(lead);
                              setNewStatus(lead.status);
                              setOpenStatusDialog(true);
                            }}
                            sx={{
                              bgcolor: '#ff9800',
                              color: '#000000',
                              '&:hover': {
                                bgcolor: '#f57c00',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <UpdateIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir lead">
                          <IconButton
                            onClick={() => {
                              setSelectedLead(lead);
                              setOpenDeleteDialog(true);
                            }}
                            sx={{
                              bgcolor: '#9e9e9e',
                              color: '#000000',
                              '&:hover': {
                                bgcolor: '#757575',
                                transform: 'scale(1.1)'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => {
          setOpenDialog(false);
          setKirvanoProducts([]);
          setKirvanoStats(null);
        }} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(31, 41, 55, 0.9)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#f44336', 
          color: '#000000',
          fontWeight: 600,
          fontSize: '1.25rem',
          borderRadius: '16px 16px 0 0'
        }}>
          📋 Detalhes do Lead
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedLead && (
            <Box>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar sx={{ 
                  bgcolor: selectedLead.control_type === 'refund_request' ? '#f44336' : '#4caf50',
                  width: 60,
                  height: 60
                }}>
                  {selectedLead.control_type === 'refund_request' ? 
                    <DollarSign sx={{ fontSize: 30 }} /> : 
                    <Phone sx={{ fontSize: 30 }} />
                  }
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom sx={{ color: '#000000' }}>
                    {selectedLead.lead_name}
                  </Typography>
                  <Chip
                    label={selectedLead.control_type === 'refund_request' ? '💰 Solicitação de Reembolso' : '📞 Call Exclusiva'}
                    color={getControlTypeColor(selectedLead.control_type) as any}
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#000000' }}>
                    📞 Informações de Contato
                  </Typography>
                  {selectedLead.lead_email && (
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="body2" color="text.secondary">Email:</Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ color: '#000000' }}>{selectedLead.lead_email}</Typography>
                    </Box>
                  )}
                  {selectedLead.lead_phone && (
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="body2" color="text.secondary">Telefone:</Typography>
                      <Typography variant="body1" fontWeight={500} sx={{ color: '#000000' }}>{selectedLead.lead_phone}</Typography>
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#000000' }}>
                    📊 Status e Prioridade
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography variant="body2" color="text.secondary">Status:</Typography>
                    <Chip
                      label={selectedLead.status}
                      color={getStatusColor(selectedLead.status) as any}
                      size="small"
                    />
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">Prioridade:</Typography>
                    <Chip
                      label={selectedLead.priority}
                      color={getPriorityColor(selectedLead.priority) as any}
                      size="small"
                    />
                  </Box>
                </Grid>
              </Grid>

              {selectedLead.notes && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#000000' }}>
                    📝 Observações
                  </Typography>
                  <Paper sx={{ 
                    p: 2, 
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 2
                  }}>
                    <Typography variant="body1" sx={{ color: '#000000' }}>{selectedLead.notes}</Typography>
                  </Paper>
                </Box>
              )}

              {/* Seção de Produtos Comprados */}
              <Box mt={3}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#000000' }}>
                  🛒 Produtos Comprados (Kirvano)
                </Typography>
                
                {!selectedLead.lead_email ? (
                  <Paper sx={{ 
                    p: 3, 
                    bgcolor: 'rgba(255, 193, 7, 0.1)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    borderRadius: 2,
                    textAlign: 'center'
                  }}>
                    <Typography variant="body1" sx={{ color: '#ffc107', fontWeight: 500 }}>
                      ⚠️ Email não cadastrado para este lead
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Para visualizar os produtos comprados, é necessário ter um email cadastrado
                    </Typography>
                  </Paper>
                ) : (
                  <>
                    {loadingProducts ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                      <CircularProgress size={24} sx={{ color: '#f44336' }} />
                      <Typography variant="body2" sx={{ ml: 2, color: '#000000' }}>
                        Carregando produtos...
                      </Typography>
                    </Box>
                  ) : kirvanoProducts.length > 0 ? (
                    <Box>
                      {/* Estatísticas */}
                      {kirvanoStats && (
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                          <Grid item xs={6} sm={3}>
                            <Paper sx={{ 
                              p: 2, 
                              bgcolor: 'rgba(76, 175, 80, 0.1)',
                              border: '1px solid rgba(76, 175, 80, 0.3)',
                              borderRadius: 2,
                              textAlign: 'center'
                            }}>
                              <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                {kirvanoStats.total_products}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Produtos
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Paper sx={{ 
                              p: 2, 
                              bgcolor: 'rgba(59, 130, 246, 0.1)',
                              border: '1px solid rgba(59, 130, 246, 0.3)',
                              borderRadius: 2,
                              textAlign: 'center'
                            }}>
                              <Typography variant="h6" sx={{ color: '#2196f3', fontWeight: 600 }}>
                                {kirvanoStats.total_purchases}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Compras
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Paper sx={{ 
                              p: 2, 
                              bgcolor: 'rgba(255, 193, 7, 0.1)',
                              border: '1px solid rgba(255, 193, 7, 0.3)',
                              borderRadius: 2,
                              textAlign: 'center'
                            }}>
                              <Typography variant="h6" sx={{ color: '#ffc107', fontWeight: 600 }}>
                                R$ {kirvanoStats.total_spent.toFixed(2)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Total Gasto
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      )}

                      {/* Lista de Produtos */}
                      <TableContainer component={Paper} sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: 2
                      }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }}>
                              <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Produto</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Preço</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Data da Compra</TableCell>
                              <TableCell sx={{ fontWeight: 600, color: '#000000' }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {kirvanoProducts.map((product, index) => (
                              <TableRow 
                                key={product.id}
                                sx={{ 
                                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' },
                                  borderBottom: index < kirvanoProducts.length - 1 ? '1px solid' : 'none',
                                  borderColor: 'rgba(255, 255, 255, 0.1)'
                                }}
                              >
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: '#000000', fontWeight: 500 }}>
                                    {product.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                    R$ {product.price.toFixed(2)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="text.secondary">
                                    {new Date(product.purchase_date).toLocaleDateString('pt-BR', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric'
                                    })}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={product.event_type === 'SALE_APPROVED' ? '✅ Aprovado' : '✅ Confirmado'}
                                    color="success"
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  ) : (
                    <Paper sx={{ 
                      p: 3, 
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: 2,
                      textAlign: 'center'
                    }}>
                      <Typography variant="body1" sx={{ color: '#000000' }}>
                        Nenhum produto encontrado para este email
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Verifique se o email está correto ou se há produtos com status sale_approved
                      </Typography>
                    </Paper>
                  )}
                  </>
                )}
              </Box>
              
              {selectedLead.status === 'pending' && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#000000' }}>
                    ⚡ Processar Lead
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Notas de Processamento"
                    placeholder="Adicione observações sobre o processamento..."
                    value={processNotes}
                    onChange={(e) => setProcessNotes(e.target.value)}
                    sx={{ 
                      mb: 2,
                      '& .MuiInputBase-input': { color: '#000000' },
                      '& .MuiInputLabel-root': { color: 'text.secondary' }
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.05)' }}>
          <Button 
            onClick={() => {
              setOpenDialog(false);
              setKirvanoProducts([]);
              setKirvanoStats(null);
            }}
            variant="outlined"
            sx={{ 
              fontWeight: 600,
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: '#000000',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Fechar
          </Button>
          {selectedLead?.status === 'pending' && (
            <Button
              variant="contained"
              onClick={() => handleProcessLead(selectedLead.id)}
              sx={{ 
                fontWeight: 600,
                px: 3,
                py: 1,
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                }
              }}
            >
              ✅ Processar Lead
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog para Alterar Status */}
      <Dialog 
        open={openStatusDialog} 
        onClose={() => setOpenStatusDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(31, 41, 55, 0.9)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#ff9800', 
          color: '#000000',
          fontWeight: 600,
          fontSize: '1.25rem',
          borderRadius: '16px 16px 0 0'
        }}>
          🔄 Alterar Status do Lead
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedLead && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: '#000000', mb: 2 }}>
                Lead: {selectedLead.lead_name}
              </Typography>
              <FormControl fullWidth>
                <InputLabel sx={{ color: 'text.secondary' }}>Novo Status</InputLabel>
                <Select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  label="Novo Status"
                  sx={{ color: '#000000' }}
                >
                  <MenuItem value="pending">⏳ Pendente</MenuItem>
                  <MenuItem value="processed">🔄 Processado</MenuItem>
                  <MenuItem value="completed">✅ Concluído</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.05)' }}>
          <Button 
            onClick={() => setOpenStatusDialog(false)}
            variant="outlined"
            sx={{ 
              fontWeight: 600,
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: '#000000',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => selectedLead && handleUpdateStatus(selectedLead.id)}
            sx={{ 
              fontWeight: 600,
              px: 3,
              py: 1,
              background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)',
              }
            }}
          >
            ✅ Atualizar Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Confirmar Exclusão */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(31, 41, 55, 0.9)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#f44336', 
          color: '#000000',
          fontWeight: 600,
          fontSize: '1.25rem',
          borderRadius: '16px 16px 0 0'
        }}>
          🗑️ Confirmar Exclusão
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {selectedLead && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ color: '#000000', mb: 2 }}>
                Tem certeza que deseja excluir este lead?
              </Typography>
              <Typography variant="body1" sx={{ color: '#000000', mb: 2 }}>
                <strong>Lead:</strong> {selectedLead.lead_name}
              </Typography>
              <Typography variant="body1" sx={{ color: '#000000', mb: 2 }}>
                <strong>Tipo:</strong> {selectedLead.control_type === 'refund_request' ? 'Reembolso' : 'Call Exclusiva'}
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                Esta ação não pode ser desfeita!
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: 'rgba(255, 255, 255, 0.05)' }}>
          <Button 
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            sx={{ 
              fontWeight: 600,
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: '#000000',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => selectedLead && handleDeleteLead(selectedLead.id)}
            sx={{ 
              fontWeight: 600,
              px: 3,
              py: 1,
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
              }
            }}
          >
            🗑️ Excluir Lead
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ControlDashboard;
